import { FC } from 'react'
import { PerModal } from '../../components/custom_components/Modal'
import { EmployeeDisplayTinyInfo, EmployeeTagInfo } from '../../types'
import { useTranslation } from 'react-i18next'
import PerText from '../../components/custom_components/Text'
import { Table } from 'antd'
import { useDateTime } from '../../shared/useDateTime'
import { useAuth } from '../../context/auth-context'
import { SortOrder } from 'antd/lib/table'
import { rankingUiProps } from './ranking.uiprops'
import { PerTag } from '../../components/custom_components/Tag'

type TableData = EmployeeTagInfo

type Props = {
  isOpen: boolean
  modalData: TableData[]
  employee?: EmployeeDisplayTinyInfo
  onCancel: () => void
}

export const RankingTagHistoryModal: FC<Props> = ({ modalData, isOpen, onCancel, employee }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)

  const { tagColor } = rankingUiProps()

  type AddedRemoveDateProps = {
    date?: string
    by?: EmployeeDisplayTinyInfo
  }

  const AddedRemoveDate: FC<AddedRemoveDateProps> = ({ date, by }) => {
    return (
      <>
        {date && (
          <>
            <PerText>{formatDateShort(date)}</PerText>
            {by && (
              <PerText appearance="label-small">{`${t('rankingView.modalTagHistory.table.by')} ${by.firstName} ${by.lastName}`}</PerText>
            )}
          </>
        )}
      </>
    )
  }

  const columns = [
    {
      key: 'addedDate',
      title: t('rankingView.modalTagHistory.table.addedDate'),
      sorter: (A: TableData, B: TableData) => {
        return A.addedDate && B.addedDate && A.addedDate.localeCompare(B.addedDate)
      },
      render: (tableData: TableData) => (
        <AddedRemoveDate date={tableData.addedDate} by={tableData.addedBy ? tableData.addedBy : undefined} />
      ),
    },
    {
      key: 'removedDate',
      title: t('rankingView.modalTagHistory.table.removeDate'),
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (A: TableData, B: TableData) => {
        const a = A.removedDate ? A.removedDate : ''
        const b = B.removedDate ? B.removedDate : ''
        return a.localeCompare(b)
      },
      render: (tableData: TableData) => (
        <AddedRemoveDate date={tableData.removedDate} by={tableData.removedBy ? tableData.removedBy : undefined} />
      ),
    },
    {
      key: 'tag',
      title: t('rankingView.modalTagHistory.table.tag'),
      render: (tableData: TableData) => (
        <PerTag color={tagColor(tableData.type)}>{t(`global.employeeTags.general.${tableData.type}`)}</PerTag>
      ),
    },
    {
      key: 'reason',
      title: t('rankingView.modalTagHistory.table.lowRemovalReason'),
      render: (tableData: TableData) => <PerText appearance="label-small">{tableData.note}</PerText>,
    },
  ]

  return (
    <PerModal
      title={{
        heading: t('rankingView.modalTagHistory.title'),
        subheading: t('rankingView.modalTagHistory.subtitle', {
          employee: `${employee ? employee.firstName : modalData[0].employee.firstName} ${
            employee ? employee.lastName : modalData[0].employee.lastName
          }`,
        }),
      }}
      visible={isOpen}
      onCancel={onCancel}
      size="large"
    >
      <Table rowKey="id" dataSource={modalData} columns={columns} pagination={false} />
    </PerModal>
  )
}
